<template>
  <div class="step-price">
    <h2 class="section-title text-subtitle-1 font-weight-medium mb-4">电价选择</h2>

    <div class="mb-2 text-body-2 font-weight-medium text-medium-emphasis">电价模式</div>
    <div class="mode-tabs d-flex flex-wrap ga-2 mb-4">
      <v-btn
        v-for="m in priceModes"
        :key="m.key"
        :variant="priceMode === m.key ? 'flat' : 'outlined'"
        :color="priceMode === m.key ? 'primary' : undefined"
        class="mode-tab text-none"
        rounded="lg"
        @click="priceMode = m.key"
      >
        {{ m.label }}
      </v-btn>
    </div>

    <v-select
      v-if="priceMode === 'agency'"
      v-model="templateId"
      :items="templateItems"
      item-title="title"
      item-value="id"
      label="电价模板"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      rounded="lg"
      class="mb-6 template-select"
    />

    <v-select
      v-if="priceMode === 'spot'"
      v-model="templateId"
      :items="spotTemplateList"
      item-title="title"
      item-value="id"
      label="电价方案"
      variant="outlined"
      density="comfortable"
      hide-details="auto"
      rounded="lg"
      class="mb-4 template-select"
    />

    <section v-if="priceMode === 'agency'" class="detail-card rounded-xl pa-4 pa-md-6">
      <div class="detail-meta text-body-2 mb-4">
        <div class="mb-1">
          <span class="text-medium-emphasis">电价名称：</span>
          <span class="font-weight-medium">{{ activeTemplate?.title }}</span>
        </div>
        <div>
          <span class="text-medium-emphasis">有效时间：</span>
          <span class="font-weight-medium">{{ activeTemplate?.effectiveRange }}</span>
        </div>
      </div>

      <v-row dense align="stretch">
        <v-col cols="12" lg="5">
          <div class="table-wrap rounded-lg border overflow-hidden">
            <table class="price-table">
              <thead>
                <tr>
                  <th>开始时间</th>
                  <th>结束时间</th>
                  <th>时段名称</th>
                  <th class="text-end">电价</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in displaySegments" :key="idx">
                  <td>{{ row.start }}</td>
                  <td>{{ row.end }}</td>
                  <td>
                    <span class="d-inline-flex align-center ga-2">
                      <i class="period-dot" :style="{ background: row.color }" aria-hidden="true" />
                      {{ row.periodName }}
                    </span>
                  </td>
                  <td class="text-end font-weight-medium">{{ formatPrice(row.price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-col>
        <v-col cols="12" lg="7">
          <div class="chart-box rounded-lg pa-2 pa-md-3">
            <div class="chart-legend d-flex justify-end flex-wrap ga-3 mb-2">
              <span v-for="leg in legendItems" :key="leg.name" class="text-caption d-inline-flex align-center ga-1">
                <i class="period-dot" :style="{ background: leg.color }" aria-hidden="true" />
                {{ leg.name }}
              </span>
            </div>
            <div ref="chartRef" class="price-chart" role="img" aria-label="分时电价柱状图" />
          </div>
        </v-col>
      </v-row>
    </section>

    <!-- 现货电价：左表右图（原型） -->
    <section v-else-if="priceMode === 'spot'" class="spot-wrap">
      <v-row dense align="stretch">
        <v-col cols="12" md="6">
          <div class="detail-card spot-card rounded-xl pa-4 pa-md-5 h-100 d-flex flex-column">
            <h3 class="text-subtitle-1 font-weight-semibold mb-1">现货电价</h3>
            <p class="text-caption text-medium-emphasis mb-4">
              电价信息即现货表格，按 15 分钟一个点维护日前/实时曲线。
            </p>

            <div class="d-flex flex-column flex-lg-row flex-wrap align-stretch align-lg-center ga-3 mb-4">
              <v-btn-toggle
                v-model="spotCurve"
                mandatory
                divided
                density="comfortable"
                color="primary"
                variant="flat"
                class="spot-toggle rounded-lg flex-shrink-0"
              >
                <v-btn value="dayAhead" class="text-none px-4">日前电价</v-btn>
                <v-btn value="realTime" class="text-none px-4">实时电价</v-btn>
              </v-btn-toggle>

              <v-text-field
                v-model="spotDate"
                type="date"
                label="日期"
                variant="outlined"
                density="compact"
                hide-details
                rounded="lg"
                prepend-inner-icon="mdi-calendar"
                class="spot-date-field flex-grow-1"
                style="max-width: 220px"
              />
            </div>

            <div class="spot-table-wrap rounded-lg border flex-grow-1 min-h-0">
              <table class="spot-table">
                <thead>
                  <tr>
                    <th class="col-idx">序号</th>
                    <th>时间点</th>
                    <th class="text-end">{{ spotMarketColumnTitle }}</th>
                    <th class="text-end">服务费(元/MWh)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in activeSpotRows" :key="spotCurve + '-' + idx">
                    <td class="text-medium-emphasis">{{ idx + 1 }}</td>
                    <td>{{ row.timeLabel }}</td>
                    <td class="text-end font-weight-medium">{{ formatSpotMwh(row.marketMwh) }}</td>
                    <td class="text-end font-weight-medium">{{ formatSpotMwh(row.serviceMwh) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="detail-card spot-card rounded-xl pa-4 pa-md-5 h-100 d-flex flex-column">
            <h3 class="text-subtitle-1 font-weight-semibold mb-4">电价预览</h3>
            <div ref="spotChartRef" class="spot-preview-chart flex-grow-1" role="img" aria-label="现货电价折线图" />
          </div>
        </v-col>
      </v-row>
    </section>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick, shallowRef } from 'vue'
import * as echarts from 'echarts'
import { getMinutesCountByTime, getTimeByMinutesCount } from '@/utils/timeHelper/index.js'

/** 与截图一致的演示数据 */
const PERIOD_COLORS = {
  valley: '#3E6AFC',
  peak: '#FF9800',
  flat: '#36c070',
}

const agencyTemplates = [
  {
    id: 'sg-202405',
    title: '2024年5月国网电价',
    effectiveRange: '2024-05-01 - 2024-05-31',
    segments: [
      { start: '00:00', end: '08:00', periodName: '低谷时段', price: 0.257869, color: PERIOD_COLORS.valley },
      { start: '08:00', end: '11:00', periodName: '高峰时段', price: 0.945521, color: PERIOD_COLORS.peak },
      { start: '11:00', end: '13:00', periodName: '低谷时段', price: 0.257869, color: PERIOD_COLORS.valley },
      { start: '13:00', end: '17:00', periodName: '高峰时段', price: 0.945521, color: PERIOD_COLORS.peak },
      { start: '17:00', end: '23:59', periodName: '平时段', price: 0.573043, color: PERIOD_COLORS.flat },
    ],
  },
  {
    id: 'sg-202406',
    title: '2024年6月国网电价',
    effectiveRange: '2024-06-01 - 2024-06-30',
    segments: [
      { start: '00:00', end: '08:00', periodName: '低谷时段', price: 0.2612, color: PERIOD_COLORS.valley },
      { start: '08:00', end: '22:00', periodName: '高峰时段', price: 0.951, color: PERIOD_COLORS.peak },
      { start: '22:00', end: '23:59', periodName: '平时段', price: 0.58, color: PERIOD_COLORS.flat },
    ],
  },
]

/** 现货模式下的方案下拉（仅选择，数据由接口/模板决定） */
const spotTemplateList = [
  { id: 'spot-1', title: '华东现货_日前', effectiveRange: '—', segments: [] },
  { id: 'spot-2', title: '华东现货_实时', effectiveRange: '—', segments: [] },
]

const priceModes = [
  { key: 'agency', label: '代理购电电价' },
  { key: 'spot', label: '现货电价' },
]

const priceMode = ref('agency')
const templateId = ref(agencyTemplates[0].id)
const chartRef = ref(null)
const chartInst = shallowRef(null)
const spotChartRef = ref(null)
const spotChartInst = shallowRef(null)

/** 现货：日前 / 实时，各 96 个 15 分钟点 */
function createSpotGrid() {
  return Array.from({ length: 96 }, (_, idx) => {
    const mins = Math.min((idx + 1) * 15, 1440)
    return {
      timeLabel: mins >= 1440 ? '24:00' : getTimeByMinutesCount(mins),
      marketMwh: 0,
      serviceMwh: 0,
    }
  })
}

const spotCurve = ref('dayAhead')
const spotDate = ref('2026-05-11')
const spotDayAheadRows = ref(createSpotGrid())
const spotRealTimeRows = ref(createSpotGrid())

const activeSpotRows = computed(() =>
  spotCurve.value === 'dayAhead' ? spotDayAheadRows.value : spotRealTimeRows.value,
)

const spotMarketColumnTitle = computed(() =>
  spotCurve.value === 'dayAhead' ? '日前市场电价(元/MWh)' : '实时市场电价(元/MWh)',
)

const templateItems = computed(() => {
  if (priceMode.value === 'agency') return agencyTemplates
  return spotTemplateList
})

const activeTemplate = computed(() => {
  const list = templateItems.value
  return list.find((t) => t.id === templateId.value) ?? list[0] ?? {}
})

watch(priceMode, () => {
  const list = templateItems.value
  templateId.value = list[0]?.id ?? ''
})

const displaySegments = computed(() => {
  if (priceMode.value !== 'agency') return []
  return activeTemplate.value?.segments ?? []
})

const legendItems = computed(() => {
  const seen = new Map()
  for (const row of displaySegments.value) {
    if (!seen.has(row.periodName)) seen.set(row.periodName, { name: row.periodName, color: row.color })
  }
  return [...seen.values()]
})

function formatPrice(p) {
  return Number(p).toFixed(6)
}

function formatSpotMwh(p) {
  return Number(p ?? 0).toFixed(4)
}

function endMinutes(endStr) {
  if (endStr === '23:59') return 1440
  return getMinutesCountByTime(endStr)
}

function buildChartOption() {
  const rows = displaySegments.value
  const peakPrice = rows.length ? Math.max(...rows.map((r) => r.price)) : 1
  const yMax = Math.max(0.2, peakPrice * 1.15)
  const chartData = rows.map((item) => {
    const st = getMinutesCountByTime(item.start)
    const et = endMinutes(item.end)
    return {
      value: [st, et, item.price, item.periodName],
      itemStyle: { color: echarts.color.modifyAlpha(item.color, 0.45) },
    }
  })

  const lineColor = '#edf2f6'
  const textColor = '#96a5b8'

  return {
    grid: { left: 48, right: 12, bottom: 36, top: 28 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1440,
      interval: 360,
      axisLabel: {
        color: textColor,
        formatter: (v) => (v >= 1440 ? '24:00' : getTimeByMinutesCount(v)),
      },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    yAxis: {
      type: 'value',
      name: '元/kWh',
      min: 0,
      max: yMax,
      nameTextStyle: { color: textColor, align: 'right' },
      axisLabel: { color: textColor },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    series: [
      {
        type: 'custom',
        data: chartData,
        renderItem: (params, api) => {
          const yValue = api.value(2)
          const start = api.coord([api.value(0), yValue])
          const size = api.size([api.value(1) - api.value(0), yValue])
          return {
            type: 'rect',
            shape: {
              x: start[0],
              y: start[1],
              width: Math.max(size[0], 1),
              height: size[1] || 8,
            },
            style: api.style({ stroke: rows[params.dataIndex]?.color, lineWidth: 1 }),
          }
        },
        label: {
          show: true,
          position: 'top',
          formatter: (p) => Number(p.value[2]).toFixed(6),
          fontSize: 10,
          color: '#5c647a',
        },
      },
    ],
    tooltip: {
      trigger: 'item',
      borderColor: '#edf2f6',
      formatter: (params) => {
        const [st, et, price, name] = params.value
        const t0 = getTimeByMinutesCount(st)
        const t1 = et >= 1440 ? '24:00' : getTimeByMinutesCount(et)
        return `${name}<br/>${t0} - ${t1}<br/>${price} 元/kWh`
      },
    },
  }
}

function renderChart() {
  if (!chartRef.value || priceMode.value !== 'agency') return
  if (!chartInst.value) chartInst.value = echarts.init(chartRef.value)
  chartInst.value.setOption(buildChartOption(), true)
  chartInst.value.resize()
}

/** 表格为 元/MWh，预览纵轴为 元/kWh（÷1000） */
function buildSpotPreviewOption() {
  const rows = activeSpotRows.value
  const lineColor = '#edf2f6'
  const textColor = '#96a5b8'
  const primary = '#2B57EE'

  const data = rows.map((row, i) => {
    const x = i * 15 + 7.5
    const kwh = (Number(row.marketMwh) + Number(row.serviceMwh)) / 1000
    return [x, kwh]
  })
  const peakKwh = data.reduce((m, d) => Math.max(m, d[1]), 0)
  /** 全 0 时与原型一致保持 0～1；有数据时略留上边距 */
  const yMax = peakKwh < 1e-9 ? 1 : Math.min(2, Math.max(0.4, Math.ceil(peakKwh * 6) / 5))

  return {
    grid: { left: 52, right: 16, bottom: 40, top: 24 },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1440,
      interval: 360,
      axisLabel: {
        color: textColor,
        formatter: (v) => (v >= 1440 ? '24:00' : getTimeByMinutesCount(v)),
      },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    yAxis: {
      type: 'value',
      name: '价格(元/kWh)',
      min: 0,
      max: yMax,
      interval: peakKwh < 1e-9 ? 0.2 : undefined,
      nameTextStyle: { color: textColor },
      axisLabel: { color: textColor, formatter: (v) => v },
      splitLine: { show: true, lineStyle: { color: lineColor } },
    },
    series: [
      {
        type: 'line',
        data,
        smooth: false,
        showSymbol: true,
        symbolSize: 4,
        lineStyle: { color: primary, width: 2 },
        itemStyle: { color: primary },
      },
    ],
    tooltip: {
      trigger: 'axis',
      borderColor: '#edf2f6',
      formatter: (items) => {
        const p = items[0]
        const x = p.value[0]
        const t = x >= 1432.5 ? '24:00' : getTimeByMinutesCount(Math.round(x))
        return `${t}<br/>${Number(p.value[1]).toFixed(6)} 元/kWh`
      },
    },
  }
}

function renderSpotChart() {
  if (!spotChartRef.value || priceMode.value !== 'spot') return
  if (!spotChartInst.value) spotChartInst.value = echarts.init(spotChartRef.value)
  spotChartInst.value.setOption(buildSpotPreviewOption(), true)
  spotChartInst.value.resize()
}

function onResize() {
  chartInst.value?.resize()
  spotChartInst.value?.resize()
}

watch([templateId, priceMode, displaySegments], () => {
  nextTick(() => {
    if (priceMode.value !== 'agency') {
      chartInst.value?.dispose()
      chartInst.value = null
      return
    }
    renderChart()
  })
})

watch(
  [priceMode, spotCurve, spotDate, spotDayAheadRows, spotRealTimeRows],
  () => {
    nextTick(() => {
      if (priceMode.value !== 'spot') {
        spotChartInst.value?.dispose()
        spotChartInst.value = null
        return
      }
      renderSpotChart()
    })
  },
  { deep: true },
)

onMounted(() => {
  nextTick(() => {
    renderChart()
    renderSpotChart()
  })
  window.addEventListener('resize', onResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
  chartInst.value?.dispose()
  chartInst.value = null
  spotChartInst.value?.dispose()
  spotChartInst.value = null
})

function getPriceDraft() {
  const t = activeTemplate.value
  if (priceMode.value === 'spot') {
    return {
      priceMode: 'spot',
      templateId: templateId.value,
      templateTitle: t?.title,
      spotDate: spotDate.value,
      spotCurve: spotCurve.value,
      dayAheadPoints: spotDayAheadRows.value.map((r) => ({ ...r })),
      realTimePoints: spotRealTimeRows.value.map((r) => ({ ...r })),
    }
  }
  return {
    priceMode: priceMode.value,
    templateId: templateId.value,
    templateTitle: t?.title,
    effectiveRange: t?.effectiveRange,
    segments: priceMode.value === 'agency' ? (t?.segments ?? []) : [],
  }
}

defineExpose({
  getPriceDraft,
})
</script>

<style scoped lang="scss">
.step-price {
  max-width: 1280px;
  margin: 0 auto;
}

.section-title {
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.mode-tab {
  border-color: color-mix(in srgb, var(--color-primary) 35%, transparent) !important;
  color: var(--color-text);
}

.mode-tab:not(.v-btn--variant-flat):hover {
  background: color-mix(in srgb, var(--color-primary) 6%, transparent);
}

.template-select {
  max-width: 420px;
}

.detail-card {
  border: 1px solid var(--color-td-border);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--color-primary) 2.5%, #fff) 0%,
    #fff 48%
  );
}

.spot-card {
  min-height: 320px;
}

.spot-toggle {
  border: 1px solid var(--color-td-border);
}

.spot-table-wrap {
  border-color: var(--color-td-border) !important;
  background: #fff;
  max-height: min(52vh, 520px);
  overflow: auto;
}

.spot-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.spot-table th,
.spot-table td {
  padding: 6px 10px;
  border-bottom: 1px solid var(--color-td-border);
  vertical-align: middle;
}

.spot-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  font-weight: 600;
  color: var(--color-text);
  background: color-mix(in srgb, var(--color-primary) 5%, #fff);
  text-align: left;
}

.spot-table th.text-end,
.spot-table td.text-end {
  text-align: right;
}

.spot-table .col-idx {
  width: 52px;
}

.spot-preview-chart {
  width: 100%;
  min-height: 280px;
  height: 360px;
}

.table-wrap {
  border-color: var(--color-td-border) !important;
  background: #fff;
}

.price-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.price-table th,
.price-table td {
  padding: 10px 12px;
  border-bottom: 1px solid var(--color-td-border);
  text-align: left;
}

.price-table th {
  font-weight: 600;
  color: var(--color-text);
  background: color-mix(in srgb, var(--color-primary) 4%, #fff);
}

.price-table tbody tr:last-child td {
  border-bottom: none;
}

.price-table td {
  color: var(--color-text);
}

.period-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.chart-box {
  border: 1px solid var(--color-td-border);
  background: #fff;
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.price-chart {
  width: 100%;
  height: 260px;
  flex: 1;
  min-height: 220px;
}
</style>
